import { FC, useState, useEffect, useRef } from "react";
import { Box, Typography, Chip, Collapse, IconButton, useMediaQuery } from "@mui/material";
import { FiChevronDown, FiChevronUp, FiTool } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { app_logo } from "@/constants";
import { tokens } from "@/tokens";

/**
 * AI Progress states - ordered by typical flow
 */
export type AIProgressState =
  | "idle"
  | "starting"
  | "thinking"
  | "using_tools"
  | "generating"
  | "complete";

interface AIProgressIndicatorProps {
  /** Current progress state */
  state: AIProgressState;
  /** Status text to display (e.g., "Analyzing documents...") */
  statusText?: string;
  /** Thinking/reasoning items to show in expandable section */
  thinkingItems?: string[];
  /** Currently active tool calls */
  activeTools?: { name: string; isActive?: boolean }[];
  /** Whether to show the component at all */
  visible?: boolean;
  /** When true, renders in static "saved" mode for persisted messages (no animations, collapsed by default) */
  isSaved?: boolean;
}

// Map technical tool names to user-friendly names
const friendlyToolName = (name: string): string => {
  const map: Record<string, string> = {
    'get_hts_hierarchy': 'HTS Database',
    'get_cbp_rulings_for_hts': 'CROSS Rulings',
    'get_cbp_rulings_for_heading6': 'CROSS Rulings',
    'get_cbp_associated_hts': 'CROSS Rulings',
    'get_cbp_rulings_for_heading': 'CROSS Rulings',
    'search_cbp_rulings': 'Rulings Search',
    'search_mcp_resources': 'Resource Search',
    'fetch_mcp_resource': 'Fetching Resource',
    'list_mcp_resources': 'Resource List',
    'query_trade_data': 'Trade Data',
    'detect_outliers': 'Anomaly Detection',
    'compare_entries': 'Entry Comparison',
    'calculate_totals': 'Calculations',
  };
  return map[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Get default status text for each state
const getDefaultStatusText = (state: AIProgressState): string => {
  switch (state) {
    case "starting": return "Starting...";
    case "thinking": return "Analyzing...";
    case "using_tools": return "Gathering information...";
    case "generating": return "Generating response...";
    case "complete": return "Done";
    default: return "";
  }
};

const AIProgressIndicator: FC<AIProgressIndicatorProps> = ({
  state,
  statusText,
  thinkingItems = [],
  activeTools = [],
  visible = true,
  isSaved = false,
}) => {
  // For saved messages, start collapsed; for live, start expanded if there's content
  const [expanded, setExpanded] = useState(!isSaved && (thinkingItems.length > 0 || activeTools.length > 0));
  const [displayedText, setDisplayedText] = useState("");
  const isMobileWidth = useMediaQuery('(max-width:600px)');
  const prevStateRef = useRef<AIProgressState>(state);

  // Animate status text changes (only for live mode)
  useEffect(() => {
    if (isSaved) {
      // For saved messages, show "AI Reasoning" as the header
      setDisplayedText("AI Reasoning");
      return;
    }
    const newText = statusText || getDefaultStatusText(state);
    if (newText !== displayedText) {
      setDisplayedText(newText);
    }
  }, [statusText, state, displayedText, isSaved]);

  // Auto-collapse when complete (only for live mode)
  useEffect(() => {
    if (!isSaved && state === "complete" && prevStateRef.current !== "complete") {
      setExpanded(false);
    }
    prevStateRef.current = state;
  }, [state, isSaved]);

  // For saved messages, always render if there's content; for live, hide on idle/complete
  if (!visible) {
    return null;
  }

  // For live mode, hide on idle or complete
  if (!isSaved && (state === "idle" || state === "complete")) {
    return null;
  }

  // For saved mode, hide if there's no content to show
  if (isSaved && thinkingItems.length === 0 && activeTools.length === 0) {
    return null;
  }

  const hasThinking = thinkingItems.length > 0;
  const hasTools = activeTools.length > 0;
  const isExpandable = hasThinking || hasTools;

  return (
    <Box
      className="ai-progress-root"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        mb: 2,
        pl: isMobileWidth ? 1 : 2,
        // Only animate on initial appearance for live mode
        ...(isSaved ? {} : {
          animation: "fadeIn 0.2s ease-in-out",
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "translateY(4px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }),
      }}
    >
      {/* Logo indicator - animated for live, static for saved */}
      <Box
        sx={{
          position: "relative",
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {/* Pulsing rings - only animate for live mode */}
        <Box
          sx={{
            position: "absolute",
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "1.5px solid",
            borderColor: isSaved ? "#9e9e9e" : (state === "using_tools" ? "#2196f3" : tokens.brand.primary),
            opacity: isSaved ? 0.4 : undefined,
            ...(isSaved ? {} : {
              animation: "pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              "@keyframes pulse-ring": {
                "0%, 100%": { opacity: 0.6, transform: "scale(1)" },
                "50%": { opacity: 0.2, transform: "scale(1.2)" },
              },
            }),
          }}
        />
        <img
          src={app_logo}
          style={{
            width: 20,
            height: 20,
            filter: isSaved ? "grayscale(0.3)" : (state === "using_tools" ? "hue-rotate(180deg)" : "none"),
            transition: "filter 0.3s ease",
          }}
          alt="AI"
        />
      </Box>

      {/* Content area */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Status line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: isExpandable ? "pointer" : "default",
          }}
          onClick={() => isExpandable && setExpanded(!expanded)}
        >
          {/* Spinner - only animate for live mode; show checkmark for saved */}
          {isSaved ? (
            <Box
              sx={{
                width: 14,
                height: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#4caf50",
                fontSize: tokens.fontSize.base,
                flexShrink: 0,
              }}
            >
              ✓
            </Box>
          ) : (
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                border: "2px solid #e0e0e0",
                borderTopColor: state === "using_tools" ? "#2196f3" : tokens.brand.primary,
                animation: "spin 0.8s linear infinite",
                flexShrink: 0,
                "@keyframes spin": {
                  from: { transform: "rotate(0deg)" },
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: isMobileWidth ? 14 : 15,
              color: "#333",
              fontWeight: tokens.fontWeight.semibold,
              transition: "all 0.2s ease",
            }}
          >
            {displayedText}
          </Typography>

          {/* Tool chips inline (when collapsed) */}
          {!expanded && hasTools && (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              {activeTools.slice(0, 2).map((tool, idx) => (
                <Chip
                  key={idx}
                  icon={
                    tool.isActive ? (
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          border: "2px solid #2196f3",
                          borderTopColor: "transparent",
                          animation: "spin 0.8s linear infinite",
                          "@keyframes spin": {
                            from: { transform: "rotate(0deg)" },
                            to: { transform: "rotate(360deg)" },
                          },
                        }}
                      />
                    ) : (
                      <FiTool size={10} />
                    )
                  }
                  label={friendlyToolName(tool.name)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    backgroundColor: tool.isActive ? "#e3f2fd" : "#f5f5f5",
                    borderColor: tool.isActive ? "#2196f3" : "#e0e0e0",
                    "& .MuiChip-icon": { ml: 0.5 },
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                  variant="outlined"
                />
              ))}
              {activeTools.length > 2 && (
                <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
                  +{activeTools.length - 2}
                </Typography>
              )}
            </Box>
          )}

          {isExpandable && (
            <IconButton
              size="small"
              sx={{ p: 0.25, color: "#666" }}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </IconButton>
          )}
        </Box>

        {/* Expandable content */}
        <Collapse in={expanded} className="ai-progress-collapse">
          <Box
            className="ai-progress-content"
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: "#f8f9fa",
              borderRadius: 1,
              borderLeft: "3px solid #2196f3",
            }}
          >
            {/* Thinking items */}
            {hasThinking && (
              <Box sx={{ mb: hasTools ? 1.5 : 0 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", display: "block", mb: 0.5, fontWeight: tokens.fontWeight.semibold }}
                >
                  {isSaved ? "Reasoning" : "Current Thinking"}
                </Typography>
                <Box
                  className="ai-progress-thinking-list"
                  sx={{
                    maxHeight: isSaved ? 200 : 150,
                    overflowY: "auto",
                    fontSize: "0.85rem",
                    color: "#333",
                    lineHeight: 1.5,
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {/* Show all items for saved mode, last 5 for live mode */}
                    {(isSaved ? thinkingItems : thinkingItems.slice(-5)).map((item, idx) => (
                      <li key={idx} style={{ margin: "4px 0" }}>
                        <Box
                          sx={{
                            "& p": { m: 0 },
                            "& p + p": { mt: 0.5 },
                            "& strong": { fontWeight: tokens.fontWeight.bold },
                            "& code": { fontFamily: "monospace", bgcolor: "#eef", px: 0.4, borderRadius: 0.5 },
                            "& ul, & ol": { my: 0.25, pl: 2 },
                            "& li": { my: 0.15 },
                          }}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{item}</ReactMarkdown>
                        </Box>
                      </li>
                    ))}
                  </ul>
                </Box>
              </Box>
            )}

            {/* Active tools */}
            {hasTools && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "#666", display: "block", mb: 0.5, fontWeight: tokens.fontWeight.semibold }}
                >
                  {isSaved ? "Tools Used" : "Tools Being Used"}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {activeTools.map((tool, idx) => (
                    <Chip
                      key={idx}
                      icon={
                        // For saved mode, never show spinning; for live mode, spin when active
                        !isSaved && tool.isActive ? (
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: "50%",
                              border: "2px solid #2196f3",
                              borderTopColor: "transparent",
                              animation: "spin 0.8s linear infinite",
                            }}
                          />
                        ) : (
                          <FiTool size={12} />
                        )
                      }
                      label={friendlyToolName(tool.name)}
                      size="small"
                      sx={{
                        backgroundColor: (!isSaved && tool.isActive) ? "#e3f2fd" : "#f5f5f5",
                        // Only animate for live mode
                        ...(!isSaved && tool.isActive ? {
                          animation: "pulse 2s ease-in-out infinite",
                          "@keyframes pulse": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.7 },
                          },
                        } : {}),
                      }}
                      variant={(!isSaved && tool.isActive) ? "filled" : "outlined"}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default AIProgressIndicator;
